import { modoDemoActivo as isDemoMode } from './demoMode';
import { isConfigured, supabase } from './supabase';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON_KEY || '';


async function readFunctionErrorPayload(error) {
  const response = error?.context || error?.response;
  if (!response || typeof response.clone !== 'function') return null;
  try {
    const text = await response.clone().text();
    return text ? JSON.parse(text) : null;
  } catch (_) {
    return null;
  }
}

// Exportado para que NADIE vuelva a escribir esta lista a mano: una copia divergida fue
// exactamente lo que dejo media aplicacion sin proteger en el control de acceso del portal.
export const ESTADOS_DE_ENVIO_ACEPTADOS = [
  'sent', 'accepted', 'accepted_by_provider', 'sent_to_meta', 'fallback_text', 'delivered', 'skipped',
];

export function isAcceptedStatus(status) {
  return ESTADOS_DE_ENVIO_ACEPTADOS.includes(String(status || '').toLowerCase());
}

function isFailedStatus(status) {
  return ['failed', 'error'].includes(String(status || '').toLowerCase());
}

function isUnknownSuccessStatusError(error) {
  const message = String(error?.message || error || '');
  return /unknown\s+2xx|2xx\s+status/i.test(message);
}

function normalizeDispatchResult(data = {}, channel = 'email', fallback = {}) {
  const event = data?.event || {};
  const providerMessageId =
    data?.message_id ||
    data?.provider_message_id ||
    data?.meta_message_id ||
    event?.provider_message_id ||
    null;
  const status = data?.status || event?.status || fallback.status || (data?.ok === false ? 'failed' : 'accepted');
  const ok = data?.ok !== false && !isFailedStatus(status) && isAcceptedStatus(status);

  return {
    ...data,
    ok,
    channel: data?.channel || event?.channel || channel,
    provider: data?.provider || event?.provider || (channel === 'email' ? 'resend' : undefined),
    status,
    message_id: providerMessageId,
    provider_message_id: providerMessageId,
    event,
    error: ok ? undefined : (data?.error || event?.last_error || undefined),
    warning: data?.warning || fallback.warning || undefined,
  };
}

async function readRecentCommunicationEvent({
  channel,
  eventKey,
  templateKey,
  recipientEmail,
  recipientPhone,
  createdAfter,
} = {}) {
  try {
    let query = supabase
      .from('communication_events')
      .select('id,event_key,template_key,channel,status,provider,provider_message_id,last_error,recipient_email,recipient_phone,created_at,sent_at')
      .eq('channel', channel)
      .gte('created_at', createdAfter)
      .order('created_at', { ascending: false })
      .limit(1);

    if (recipientEmail) query = query.eq('recipient_email', recipientEmail);
    if (recipientPhone) query = query.eq('recipient_phone', recipientPhone);
    if (eventKey) query = query.eq('event_key', eventKey);
    if (templateKey) query = query.eq('template_key', templateKey);

    const { data, error } = await query;
    if (error) return null;
    return data?.[0] || null;
  } catch (_) {
    return null;
  }
}

export async function dispatchCriticalCommunication({
  eventKey,
  channel = 'email',
  templateKey,
  recipientEmail,
  recipientPhone,
  payload = {},
  clienteId = null,
  proyectoId = null,
  contratoId = null,
  cobroId = null,
  isDemo = false,
  maxAttempts = 3,
} = {}) {
  const demo = Boolean(isDemo || isDemoMode() || payload?.is_demo);
  const normalizedChannel = String(channel || 'email').toLowerCase();

  if (demo) {
    console.info('[communications] Modo demo: comunicacion critica omitida.', {
      eventKey,
      channel: normalizedChannel,
      templateKey,
    });
    return { ok: true, status: 'skipped', demo: true, reason: 'Modo demo: no se envio servicio real.' };
  }

  if (!isConfigured) {
    const error = 'Supabase no esta configurado; no se registro ni envio comunicacion critica.';
    console.error('[communications]', error, { eventKey, channel: normalizedChannel, templateKey });
    return { ok: false, status: 'failed', error };
  }

  try {
    const invokedAt = new Date(Date.now() - 1000).toISOString();
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;
    if (!accessToken) {
      return {
        ok: false,
        status: 'failed',
        code: 'missing_auth',
        error: 'No hay una sesion activa para enviar comunicaciones criticas.',
      };
    }
    const body = {
        event_key: eventKey || templateKey || 'critical_communication',
        channel: normalizedChannel,
        template_key: templateKey,
        recipient_email: recipientEmail,
        recipient_phone: recipientPhone,
        payload,
        cliente_id: clienteId,
        proyecto_id: proyectoId,
        contrato_id: contratoId,
        cobro_id: cobroId,
        is_demo: demo,
        max_attempts: maxAttempts,
      };

    if (SUPABASE_URL && SUPABASE_ANON) {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/dispatch-critical-communication`, {
        method: 'POST',
        headers: {
          apikey: SUPABASE_ANON,
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      const text = await response.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (_) {
        data = { ok: response.ok, status: response.ok ? 'accepted' : 'failed', raw: text };
      }
      if (!response.ok || data?.ok === false) {
        return normalizeDispatchResult({
          ...data,
          ok: false,
          status: data?.status || 'failed',
          error: data?.error || data?.message || `dispatch-critical-communication ${response.status}`,
        }, normalizedChannel);
      }
      return normalizeDispatchResult(data || { ok: true }, normalizedChannel);
    }

    const { data, error } = await supabase.functions.invoke('dispatch-critical-communication', {
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
      body,
    });
    if (error) {
      const parsed = await readFunctionErrorPayload(error);
      if (parsed && parsed.ok !== false && !isFailedStatus(parsed.status)) {
        return normalizeDispatchResult(parsed, normalizedChannel, { status: 'accepted' });
      }
      if (isUnknownSuccessStatusError(error)) {
        const recentEvent = await readRecentCommunicationEvent({
          channel: normalizedChannel,
          eventKey: eventKey || templateKey || 'critical_communication',
          templateKey,
          recipientEmail,
          recipientPhone,
          createdAfter: invokedAt,
        });
        if (recentEvent) {
          return normalizeDispatchResult({
            ok: recentEvent.status !== 'failed',
            status: recentEvent.status,
            event: recentEvent,
            provider: recentEvent.provider,
            provider_message_id: recentEvent.provider_message_id,
            warning: 'recovered_from_unknown_2xx',
          }, normalizedChannel);
        }
        console.warn('[communications] Edge Function devolvio 2xx sin cuerpo normalizado y no se encontro evento de entrega.', {
          eventKey,
          channel: normalizedChannel,
          message: error?.message || String(error),
        });
        return normalizeDispatchResult({
          ok: true,
          status: 'accepted_by_provider',
          warning: 'missing_normalized_body',
        }, normalizedChannel);
      }
      throw error;
    }
    if (data?.ok === false) {
      console.error('[communications] dispatch-critical-communication fallo:', data);
      return normalizeDispatchResult(data, normalizedChannel);
    }
    return normalizeDispatchResult(data || { ok: true }, normalizedChannel);
  } catch (error) {
    console.error('[communications] Error enviando comunicacion critica:', error);
    return { ok: false, status: 'failed', error: error?.message || String(error) };
  }
}

export function dispatchCriticalEmail(eventKey, templateKey, to, params = {}, refs = {}) {
  return dispatchCriticalCommunication({
    eventKey,
    channel: 'email',
    templateKey,
    recipientEmail: to,
    payload: params,
    ...refs,
  });
}
